import { Injectable, NgZone } from '@angular/core';

/**
 * Cross-table visual linking for the v2 Data Categorization page.
 *
 * The Document Details table (source rows) and the Financial Template table
 * render the *same* categorized `Row` objects, so a row can be matched across
 * both panes by its `uuid` (exposed as `data-row-key`). This service is the
 * single home for the shared DOM behaviors that link the two tables:
 *
 *  - `setHoveredKey`  — bidirectional hover highlight (reuses the tables'
 *    existing `:hover` styling via the `.row-linked-hover` class).
 *  - `scrollRowIntoView` — bring a matching row into view (no highlight).
 *  - `flashRow` / `flashRows` — scroll to a row and highlight it green for 3s
 *    (used by the "View →" tooltip action and by Invert Values, which reveal
 *    the categorized row's twin in the Financial Template table).
 *
 * All DOM work runs via `NgZone.runOutsideAngular`, so hover never triggers
 * change detection on these large grids.
 */
@Injectable({ providedIn: 'root' })
export class SpreadingRowLinkService {
  private static readonly HOVER_CLASS = 'row-linked-hover';
  private static readonly FLASH_CLASS = 'row-linked-flash';
  private static readonly FLASH_DURATION_MS = 3000;
  private static readonly SCROLL_OPTIONS: ScrollIntoViewOptions = {
    block: 'center',
    inline: 'nearest',
    behavior: 'smooth',
  };

  private _hoveredKey: string | null = null;
  private _flashedElements: HTMLElement[] = [];
  private _flashTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private ngZone: NgZone) {}

  /**
   * Highlight every row matching `key` across both tables, clearing any
   * previously highlighted rows. Passing `null` clears the highlight.
   */
  setHoveredKey(key: string | null): void {
    if (key === this._hoveredKey) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this._toggleHoverClass(this._hoveredKey, false);
      this._hoveredKey = key;
      this._toggleHoverClass(key, true);
    });
  }

  /** Smoothly scroll the first element matching `selector` into view. */
  scrollRowIntoView(selector: string): void {
    this.ngZone.runOutsideAngular(() => {
      const element = document.querySelector<HTMLElement>(selector);
      element?.scrollIntoView(SpreadingRowLinkService.SCROLL_OPTIONS);
    });
  }

  /**
   * Scroll the first element matching `selector` into view and flash it green
   * for 3 seconds. Any in-progress flash is cleared first. Pass `root` to scope
   * the lookup to a single table, since a categorized row carries the same
   * `data-row-key` in both.
   */
  flashRow(selector: string, root: ParentNode = document): void {
    this.flashRows([selector], root);
  }

  /**
   * Flash every element matching one of `selectors` green for 3 seconds and
   * scroll only to the top-most one. Pass `root` to scope the lookup to a table.
   */
  flashRows(selectors: string[], root: ParentNode = document): void {
    this.ngZone.runOutsideAngular(() => {
      const elements = this._findInDocumentOrder(selectors, root);
      if (elements.length === 0) {
        return;
      }
      this._clearFlash();
      elements[0].scrollIntoView(SpreadingRowLinkService.SCROLL_OPTIONS);
      this._restartFlashAnimation(elements);
      this._flashedElements = elements;
      this._flashTimeoutId = setTimeout(
        () => this._clearFlash(),
        SpreadingRowLinkService.FLASH_DURATION_MS
      );
    });
  }

  private _findInDocumentOrder(selectors: string[], root: ParentNode): HTMLElement[] {
    const found = new Set<HTMLElement>();
    selectors.forEach((selector) => {
      const element = root.querySelector<HTMLElement>(selector);
      if (element) {
        found.add(element);
      }
    });
    return Array.from(found).sort((a, b) =>
      a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
    );
  }

  /**
   * Remove the class, flush layout once for the whole set, then re-add it, so
   * the animation replays even when scrollIntoView is a no-op.
   */
  private _restartFlashAnimation(elements: HTMLElement[]): void {
    elements.forEach((element) =>
      element.classList.remove(SpreadingRowLinkService.FLASH_CLASS)
    );
    void elements[0].offsetWidth;
    elements.forEach((element) =>
      element.classList.add(SpreadingRowLinkService.FLASH_CLASS)
    );
  }

  private _toggleHoverClass(key: string | null, add: boolean): void {
    if (!key) {
      return;
    }
    const nodes = document.querySelectorAll<HTMLElement>(`[data-row-key="${key}"]`);
    nodes.forEach((node) =>
      node.classList.toggle(SpreadingRowLinkService.HOVER_CLASS, add)
    );
  }

  private _clearFlash(): void {
    if (this._flashTimeoutId !== null) {
      clearTimeout(this._flashTimeoutId);
      this._flashTimeoutId = null;
    }
    this._flashedElements.forEach((element) =>
      element.classList.remove(SpreadingRowLinkService.FLASH_CLASS)
    );
    this._flashedElements = [];
  }
}
